import * as THREE from "three";
import { anonymousIdSuffix, avatarFor } from "../avatar";
import { t } from "../i18n";

const AVATAR_PX = 40;
const SELECT_OVERLAP_PX = 10;
const SEP_ITERATIONS = 2;
const SEP_REST_PX = AVATAR_PX * 0.9;
const MAX_SEP_PX = AVATAR_PX * 1.5;
const OCCUPY_Z = 52;
const IDLE_Z = 50;

export interface AvatarTarget {
  uid: string;
  color: string;
  name: string;
  box: THREE.Box3;
}

export interface PlayerAvatarsUpdate {
  camera: THREE.Camera;
  remotes: AvatarTarget[];
  selfUid: string;
  selfColor: string;
  selfName: string;
  selfOccupiedBox: THREE.Box3 | null;
}

export interface PlayerAvatars {
  /** Per-frame: place every avatar at its object's top-left, separate overlaps, and ease the actual position toward the result. */
  update(u: PlayerAvatarsUpdate): void;
  /** Spawns a rising-and-fading emote glyph over the avatar for `uid`; no-op when that avatar is absent or hidden. */
  playEmote(uid: string, emoji: string): void;
  /** Tears down every avatar and resets state; used on room leave. */
  clear(): void;
}

interface Avatar {
  el: HTMLDivElement;
  img: HTMLImageElement;
  label: HTMLDivElement;
  imgKey: string;
  name: string;
}

interface Slot {
  avatar: Avatar;
  baseX: number;
  baseY: number;
  sepX: number;
  sepY: number;
  occupying: boolean;
}

function makeAvatar(): Avatar {
  const el = document.createElement("div");
  el.className = "player-avatar";
  el.style.display = "none";
  const img = document.createElement("img");
  img.className = "player-avatar-img";
  img.alt = "";
  img.draggable = false;
  const label = document.createElement("div");
  label.className = "player-avatar-name";
  el.append(img, label);
  document.body.appendChild(el);
  return { el, img, label, imgKey: "", name: "" };
}

function setAvatar(a: Avatar, uid: string, color: string, name: string, sizePx: number): void {
  const key = `${uid}|${color}`;
  if (a.imgKey !== key) {
    a.img.src = avatarFor(uid, color, sizePx);
    a.imgKey = key;
  }
  a.el.style.borderColor = color;
  const display = name === "" ? `${t("multiplayer.anonymousName")}_${anonymousIdSuffix(uid)}` : name;
  if (a.name !== display) {
    a.label.textContent = display;
    a.label.style.display = "";
    a.name = display;
  }
}

/**
 * Renders each room participant's avatar as a screen overlay anchored to a world
 * point. While a player occupies (holds the edit claim on) a selected object, the
 * anchor tracks that object and the avatar sits at its top-left, overlapping the
 * outline; once the player stops occupying, the anchor freezes at that last world
 * point (so the avatar stays where the player left it, follows the camera, and
 * hides only when panned off-screen). Occupancy therefore only governs whether the
 * anchor updates — every avatar otherwise behaves the same: overlapping avatars push
 * apart by half their overlap each (bounded), and occupiers draw in front. Positions
 * are set straight from the projected anchor each frame (no easing) so they never lag
 * or drift while the camera zooms. A remote player only has an avatar while it holds a
 * claim (claims are synced; idle remotes are not), so the local player's frozen
 * last-position is its own view only.
 */
const SELF_LONG_PRESS_MS = 400;
const SELF_MOVE_CANCEL_PX = 10;
const EMOTE_ARC_DEG = 90;
const EMOTE_START_R = 22;
const EMOTE_END_R = 84;
const EMOTE_RISE_MS = 2400;

/**
 * Wires an avatar's `el` to fire `onActivate` on both a click (mouse, or a quick
 * touch tap, which the browser follows with a click) and a long-press that fires at
 * the threshold while the pointer is still down (cancelled if it moves past the move
 * threshold). When the long-press fires it suppresses the trailing synthetic click so
 * a single hold fires once. Shared by the self avatar (opens the emote menu) and each
 * remote avatar (opens the kick UI).
 */
function attachActivate(el: HTMLDivElement, onActivate: (clientX: number, clientY: number, pointerId?: number) => void): void {
  let pressTimer: number | null = null;
  let longFired = false;
  let startX = 0;
  let startY = 0;
  let activePointerId = 0;
  const clearTimer = (): void => {
    if (pressTimer !== null) {
      window.clearTimeout(pressTimer);
      pressTimer = null;
    }
  };
  el.addEventListener("pointerdown", (ev: PointerEvent): void => {
    longFired = false;
    startX = ev.clientX;
    startY = ev.clientY;
    activePointerId = ev.pointerId;
    clearTimer();
    pressTimer = window.setTimeout((): void => {
      pressTimer = null;
      longFired = true;
      onActivate(startX, startY, activePointerId);
    }, SELF_LONG_PRESS_MS);
  });
  el.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (pressTimer !== null && Math.hypot(ev.clientX - startX, ev.clientY - startY) > SELF_MOVE_CANCEL_PX) {
      clearTimer();
    }
  });
  el.addEventListener("pointerup", clearTimer);
  el.addEventListener("pointercancel", clearTimer);
  el.addEventListener("click", (ev: MouseEvent): void => {
    if (longFired) {
      longFired = false;
      return;
    }
    onActivate(ev.clientX, ev.clientY);
  });
  el.addEventListener("contextmenu", (ev: Event): void => ev.preventDefault());
}

/** Makes an avatar's `el` tappable: enables pointer hits, suppresses touch scroll/long-press menus, and wires the activate gesture. */
function makeActivatable(el: HTMLDivElement, onActivate: (clientX: number, clientY: number, pointerId?: number) => void): void {
  el.style.pointerEvents = "auto";
  el.style.touchAction = "none";
  attachActivate(el, onActivate);
}

export function initPlayerAvatars(
  onSelfActivate?: (clientX: number, clientY: number, pointerId?: number) => void,
  onPeerActivate?: (uid: string, clientX: number, clientY: number, pointerId?: number) => void,
): PlayerAvatars {
  const remotes = new Map<string, Avatar>();
  const renderPx = Math.ceil(AVATAR_PX * (window.devicePixelRatio || 1));
  const ndc = new THREE.Vector3();
  const corner = new THREE.Vector3();
  const center = new THREE.Vector3();
  const selfAnchor = new THREE.Vector3();
  let selfHasAnchor = false;
  let selfUid = "";
  const self = makeAvatar();
  if (onSelfActivate !== undefined) {
    makeActivatable(self.el, onSelfActivate);
  }

  function projectPoint(v: THREE.Vector3, camera: THREE.Camera, w: number, h: number): { x: number; y: number; onScreen: boolean } {
    ndc.copy(v).project(camera);
    return {
      x: (ndc.x * 0.5 + 0.5) * w,
      y: (-ndc.y * 0.5 + 0.5) * h,
      onScreen: ndc.z < 1 && Math.abs(ndc.x) <= 1 && Math.abs(ndc.y) <= 1,
    };
  }

  /** Screen-space top-left (inset by the overlap) of a world AABB, and whether it is on camera. */
  function projectBox(box: THREE.Box3, camera: THREE.Camera, w: number, h: number): { tlX: number; tlY: number; onScreen: boolean } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let behind = 0;
    for (let i = 0; i < 8; i++) {
      corner.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z);
      ndc.copy(corner).project(camera);
      if (ndc.z > 1) {
        behind++;
      }
      const sx = (ndc.x * 0.5 + 0.5) * w;
      const sy = (-ndc.y * 0.5 + 0.5) * h;
      minX = Math.min(minX, sx);
      minY = Math.min(minY, sy);
      maxX = Math.max(maxX, sx);
      maxY = Math.max(maxY, sy);
    }
    const inset = AVATAR_PX / 2 - SELECT_OVERLAP_PX;
    const onScreen = behind < 8 && maxX > 0 && minX < w && maxY > 0 && minY < h;
    return { tlX: minX - inset, tlY: minY - inset, onScreen };
  }

  function separate(slots: Slot[]): void {
    for (let iter = 0; iter < SEP_ITERATIONS; iter++) {
      for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
          let dx = slots[i].sepX - slots[j].sepX;
          let dy = slots[i].sepY - slots[j].sepY;
          let dist = Math.hypot(dx, dy);
          if (dist >= SEP_REST_PX) {
            continue;
          }
          if (dist < 1e-3) {
            dx = i - j;
            dy = (i + j) % 2 === 0 ? 1 : -1;
            dist = Math.hypot(dx, dy);
          }
          const push = (SEP_REST_PX - dist) / 2 / dist;
          slots[i].sepX += dx * push;
          slots[i].sepY += dy * push;
          slots[j].sepX -= dx * push;
          slots[j].sepY -= dy * push;
        }
      }
    }
    for (const s of slots) {
      s.sepX = s.baseX + THREE.MathUtils.clamp(s.sepX - s.baseX, -MAX_SEP_PX, MAX_SEP_PX);
      s.sepY = s.baseY + THREE.MathUtils.clamp(s.sepY - s.baseY, -MAX_SEP_PX, MAX_SEP_PX);
    }
  }

  function place(a: Avatar, x: number, y: number, z: number): void {
    a.el.style.display = "";
    a.el.style.zIndex = String(z);
    a.el.style.left = `${x - AVATAR_PX / 2}px`;
    a.el.style.top = `${y - AVATAR_PX / 2}px`;
  }

  function hide(a: Avatar): void {
    a.el.style.display = "none";
  }

  function update(u: PlayerAvatarsUpdate): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const slots: Slot[] = [];

    const seen = new Set<string>();
    for (const target of u.remotes) {
      seen.add(target.uid);
      let a = remotes.get(target.uid);
      if (a === undefined) {
        a = makeAvatar();
        remotes.set(target.uid, a);
        if (onPeerActivate !== undefined) {
          const uid = target.uid;
          makeActivatable(a.el, (clientX: number, clientY: number, pointerId?: number): void => onPeerActivate(uid, clientX, clientY, pointerId));
        }
      }
      setAvatar(a, target.uid, target.color, target.name, renderPx);
      const r = projectBox(target.box, u.camera, w, h);
      if (r.onScreen) {
        slots.push({ avatar: a, baseX: r.tlX, baseY: r.tlY, sepX: r.tlX, sepY: r.tlY, occupying: true });
      } else {
        hide(a);
      }
    }
    for (const [uid, a] of remotes) {
      if (!seen.has(uid)) {
        a.el.remove();
        remotes.delete(uid);
      }
    }

    selfUid = u.selfUid;
    setAvatar(self, u.selfUid, u.selfColor, u.selfName, renderPx);
    if (u.selfOccupiedBox !== null) {
      const r = projectBox(u.selfOccupiedBox, u.camera, w, h);
      u.selfOccupiedBox.getCenter(center);
      ndc.copy(center).project(u.camera);
      selfAnchor.set((r.tlX / w) * 2 - 1, 1 - (r.tlY / h) * 2, ndc.z).unproject(u.camera);
      selfHasAnchor = true;
      if (r.onScreen) {
        slots.push({ avatar: self, baseX: r.tlX, baseY: r.tlY, sepX: r.tlX, sepY: r.tlY, occupying: true });
      } else {
        hide(self);
      }
    } else if (selfHasAnchor) {
      const p = projectPoint(selfAnchor, u.camera, w, h);
      if (p.onScreen) {
        slots.push({ avatar: self, baseX: p.x, baseY: p.y, sepX: p.x, sepY: p.y, occupying: false });
      } else {
        hide(self);
      }
    } else {
      hide(self);
    }

    separate(slots);
    for (const s of slots) {
      place(s.avatar, s.sepX, s.sepY, s.occupying ? OCCUPY_Z : IDLE_Z);
    }
  }

  function playEmote(uid: string, emoji: string): void {
    const a = uid === selfUid ? self : remotes.get(uid);
    if (a === undefined || a.el.style.display === "none") {
      return;
    }
    const span = document.createElement("span");
    span.className = "player-avatar-emote";
    span.textContent = emoji;
    a.el.appendChild(span);
    const angle = (-90 + (Math.random() - 0.5) * EMOTE_ARC_DEG) * (Math.PI / 180);
    const ux = Math.cos(angle);
    const uy = Math.sin(angle);
    const at = (r: number): string => `translate(calc(-50% + ${(ux * r).toFixed(1)}px), calc(-50% + ${(uy * r).toFixed(1)}px))`;
    const anim = span.animate(
      [
        { transform: `${at(EMOTE_START_R)} scale(0.6)`, opacity: 0 },
        { transform: `${at(EMOTE_START_R + 6)} scale(1)`, opacity: 1, offset: 0.08 },
        { opacity: 1, offset: 0.75 },
        { transform: `${at(EMOTE_END_R)} scale(1)`, opacity: 0 },
      ],
      { duration: EMOTE_RISE_MS, easing: "ease-out", fill: "forwards" },
    );
    anim.onfinish = (): void => span.remove();
    a.img.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.3)", offset: 0.45 }, { transform: "scale(1)" }],
      { duration: 340, easing: "ease-out" },
    );
  }

  function clear(): void {
    for (const [, a] of remotes) {
      a.el.remove();
    }
    remotes.clear();
    hide(self);
    selfHasAnchor = false;
  }

  return { update, playEmote, clear };
}
